import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ordersApi } from '../services/api';

interface OrderDetailData {
  id: string;
  orderNumber: string;
  businessId?: string;
  business?: { name?: string; mangoId?: string };
  businessName?: string;
  items?: Array<{
    id: string;
    name?: string;
    productName?: string;
    product?: { name?: string };
    quantity: number;
    price: number;
    total?: number;
    productImage?: string;
  }>;
  subtotal?: number;
  tax?: number;
  shipping?: number;
  total?: number;
  status?: string;
  fulfillmentStatus?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  createdAt?: string;
  shippedAt?: string;
}

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<OrderDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) loadOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadOrder depends on id, stable
  }, [id]);

  const loadOrder = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await ordersApi.getOrderDetails(id);
      const o = res?.order ?? res?.data;
      setOrder(o ?? null);
      if (!o && res?.error) setError(res.error);
    } catch (err) {
      console.error('Load order failed:', err);
      setError('Failed to load order');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (s: string) => {
    switch (s) {
      case 'delivered': return '#28a745';
      case 'shipped': return '#17a2b8';
      case 'processing': case 'confirmed': return '#ffc107';
      case 'pending': return '#6c757d';
      case 'cancelled': case 'refunded': return '#dc3545';
      default: return '#666';
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 32 }}>⏳</div>
        <p style={{ color: '#666', marginTop: 12 }}>Loading order...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div>
        <Link to="/orders" style={{ color: '#667eea', fontSize: 14, marginBottom: 16, display: 'inline-block' }}>← Back to Orders</Link>
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: 24, borderRadius: 12 }}>
          {error || 'Order not found'}
        </div>
      </div>
    );
  }

  const status = order.status ?? order.fulfillmentStatus ?? 'pending';
  const businessName = order.businessName ?? order.business?.name ?? 'Business';
  const items = order.items ?? [];
  const total = order.total ?? (order.subtotal ?? 0) + (order.tax ?? 0) + (order.shipping ?? 0);

  return (
    <div>
      <Link to="/orders" style={{ color: '#667eea', fontSize: 14, marginBottom: 16, display: 'inline-block' }}>← Back to Orders</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: '#333' }}>Order {order.orderNumber}</h1>
          <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: 14 }}>{businessName}</p>
          {order.createdAt && (
            <p style={{ margin: '4px 0 0 0', color: '#999', fontSize: 13 }}>{new Date(order.createdAt).toLocaleDateString(undefined, { dateStyle: 'long' })}</p>
          )}
        </div>
        <span style={{
          padding: '8px 16px',
          background: getStatusColor(status) + '20',
          color: getStatusColor(status),
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          textTransform: 'capitalize',
        }}>
          {status}
        </span>
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 16 }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: 16, color: '#333' }}>Items</h2>
        {items.map((item) => (
          <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #eee' }}>
            <div>
              <span style={{ fontWeight: 600, color: '#333' }}>{item.name ?? item.productName ?? item.product?.name ?? 'Item'}</span>
              <span style={{ marginLeft: 8, color: '#666', fontSize: 14 }}>× {item.quantity}</span>
            </div>
            <span style={{ fontWeight: 600, color: '#333' }}>${(item.total ?? (item.price ?? 0) * (item.quantity ?? 1)).toFixed(2)}</span>
          </div>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {order.subtotal != null && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><span style={{ color: '#666' }}>Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>}
        {order.tax != null && order.tax > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><span style={{ color: '#666' }}>Tax</span><span>${order.tax.toFixed(2)}</span></div>}
        {order.shipping != null && order.shipping > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><span style={{ color: '#666' }}>Shipping</span><span>${order.shipping.toFixed(2)}</span></div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, paddingTop: 12, borderTop: '1px solid #eee', fontWeight: 700, fontSize: 18 }}><span>Total</span><span>${total.toFixed(2)}</span></div>
        {(order.trackingNumber || order.trackingUrl) && (
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #eee' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Tracking</h3>
            {order.trackingUrl ? (
              <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>{order.trackingNumber || 'Track package'}</a>
            ) : (
              <span>{order.trackingNumber}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetail;
